import React, { useState, useEffect } from 'react';
import { Table, Form, Alert } from 'react-bootstrap';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import Modal from '../../components/UI/Modal';
import { userService, UserData, UserCreateData } from '../../api/userService';

const UserManagementContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin: 0;
    font-size: ${theme.fontSizes.xlarge};
    color: ${theme.colors.dark};
  }
`;

const StyledTable = styled(Table)`
  background-color: white;
  border-radius: ${theme.radii.medium};
  box-shadow: ${theme.shadows.small};
  
  th {
    background-color: ${theme.colors.light};
    border-bottom: 2px solid ${theme.colors.light};
    font-weight: 600;
    color: ${theme.colors.dark};
  }
  
  td {
    vertical-align: middle;
    color: ${theme.colors.text};
  }
`;

interface CustomButtonProps {
  variant?: 'primary' | 'secondary' | 'warning' | 'danger';
  size?: 'sm' | 'lg';
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}

const CustomButton = styled.button<CustomButtonProps>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${({ size = 'lg' }) => 
    size === 'sm' ? `${theme.spacing.xs} ${theme.spacing.md}` : 
    `${theme.spacing.sm} ${theme.spacing.lg}`
  };
  font-weight: 500;
  border-radius: ${theme.radii.small};
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  font-size: ${({ size = 'lg' }) => 
    size === 'sm' ? theme.fontSizes.small : 
    theme.fontSizes.medium
  };
  
  svg {
    width: ${({ size = 'lg' }) => size === 'sm' ? '14px' : '16px'};
    height: ${({ size = 'lg' }) => size === 'sm' ? '14px' : '16px'};
  }
  
  ${({ variant = 'primary' }) => {
    switch (variant) {
      case 'primary':
        return `
          background-color: ${theme.colors.primary};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.secondary};
          }
          
          &:disabled {
            background-color: ${theme.colors.gray};
            cursor: not-allowed;
          }
        `;
      case 'secondary':
        return `
          background-color: ${theme.colors.light};
          color: ${theme.colors.dark};
          
          &:hover {
            background-color: ${theme.colors.gray};
          }
          
          &:disabled {
            background-color: ${theme.colors.light};
            color: ${theme.colors.gray};
            cursor: not-allowed;
          }
        `;
      case 'warning':
        return `
          background-color: ${theme.colors.warning};
          color: ${theme.colors.dark};
          
          &:hover {
            background-color: ${theme.colors.accent};
          }
          
          &:disabled {
            background-color: ${theme.colors.light};
            color: ${theme.colors.gray};
            cursor: not-allowed;
          }
        `;
      case 'danger':
        return `
          background-color: ${theme.colors.danger};
          color: white;
          
          &:hover {
            background-color: ${theme.colors.danger};
            opacity: 0.9;
          }
          
          &:disabled {
            background-color: ${theme.colors.gray};
            cursor: not-allowed;
          }
        `;
    }
  }}
`;

const StyledForm = styled(Form)`
  .form-label {
    font-weight: 500;
    color: ${theme.colors.dark};
    margin-bottom: ${theme.spacing.xs};
  }
  
  .form-control, .form-select {
    border-radius: ${theme.radii.small};
    border: 1px solid ${theme.colors.light};
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: ${theme.fontSizes.medium};
    
    &:focus {
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
    }
  }
  
  .form-text {
    color: ${theme.colors.gray};
    font-size: ${theme.fontSizes.small};
    margin-top: ${theme.spacing.xs};
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: flex-end;
  margin-top: ${theme.spacing.lg};
`;

interface User extends UserData {
  id: string | number;
  name: string;
  email: string;
  role: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [newUser, setNewUser] = useState<UserCreateData>({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  const [resetPassword, setResetPassword] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('Fetching users...');
      const response = await userService.getUsers();
      console.log('Users response:', response);
      
      // Map the response to our User interface
      const mappedUsers = response.data.data.map((user: UserData): User => ({
        ...user,
        id: user.id,
        name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || '',
        email: user.email || '',
        role: user.role || (user.roles && user.roles[0]) || 'user'
      }));
      
      console.log('Mapped users:', mappedUsers);
      setUsers(mappedUsers);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError('Failed to fetch users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('handleAddUser called', newUser);
    setLoading(true);
    try {
      // Create the user using the API
      await userService.createUser(newUser);
      console.log('userService.createUser finished');
      
      // Clear the form and close the modal
      setNewUser({
        name: '',
        email: '',
        password: '',
        role: 'user'
      });
      setShowAddModal(false);
      
      // Add a small delay before fetching users to ensure backend processing is complete
      setTimeout(async () => {
        try {
          // Refresh the user list
          await fetchUsers();
          setError(null);
        } catch (err) {
          console.error('Error fetching updated user list:', err);
          setError('User was created but the list could not be refreshed. Please reload the page.');
        }
      }, 1000);
      
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Failed to create user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    if (resetPassword.password !== resetPassword.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      // Reset the user's password using the API
      await userService.resetPassword(selectedUser.id, resetPassword.password);
      
      // Clear the form and close the modal
      setResetPassword({
        password: '',
        confirmPassword: ''
      });
      setShowResetModal(false);
      setSelectedUser(null);
      setError(null);
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      // Delete the user using the API
      await userService.deleteUser(selectedUser.id);
      
      // Close the modal and refresh the user list
      setShowDeleteModal(false);
      setSelectedUser(null);
      await fetchUsers();
      setError(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserManagementContainer>
      <Header>
        <h2>User Management</h2>
        <CustomButton 
          variant="primary" 
          onClick={() => setShowAddModal(true)}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Add New User
        </CustomButton>
      </Header>

      {error && <Alert variant="danger">{error}</Alert>}

      <StyledTable striped bordered hover>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>{user.name}</td>
              <td>{user.email}</td>
              <td>{user.role}</td>
              <td>
                <CustomButton
                  variant="warning"
                  size="sm"
                  onClick={() => {
                    setSelectedUser(user);
                    setShowResetModal(true);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                  </svg>
                  Reset Password
                </CustomButton>
                <CustomButton
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setSelectedUser(user);
                    setShowDeleteModal(true);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                  Delete
                </CustomButton>
              </td>
            </tr>
          ))}
        </tbody>
      </StyledTable>

      {/* Add User Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setNewUser({
            name: '',
            email: '',
            password: '',
            role: 'user'
          });
        }}
        title="Add New User"
      >
        <StyledForm onSubmit={handleAddUser}>
          <FormGroup>
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              placeholder="Enter user's full name"
              required
            />
          </FormGroup>
          <FormGroup>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="Enter user's email address"
              required
            />
          </FormGroup>
          <FormGroup>
            <Form.Label>Password</Form.Label>
            <Form.Control
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              placeholder="Enter a secure password"
              required
              minLength={8}
            />
            <Form.Text>
              Password must be at least 8 characters long
            </Form.Text>
          </FormGroup>
          <FormGroup>
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </Form.Select>
            <Form.Text>
              Admins have full access to manage courses and users
            </Form.Text>
          </FormGroup>
          <ButtonGroup>
            <CustomButton 
              variant="secondary" 
              type="button"
              onClick={() => {
                setShowAddModal(false);
                setNewUser({
                  name: '',
                  email: '',
                  password: '',
                  role: 'user'
                });
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton 
              variant="primary" 
              type="submit" 
              disabled={loading || !newUser.name || !newUser.email || !newUser.password}
            >
              {loading ? 'Adding...' : 'Add User'}
            </CustomButton>
          </ButtonGroup>
        </StyledForm>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetModal}
        onClose={() => {
          setShowResetModal(false);
          setResetPassword({
            password: '',
            confirmPassword: ''
          });
        }}
        title={`Reset Password for ${selectedUser?.name}`}
      >
        <StyledForm onSubmit={handleResetPassword}>
          <FormGroup>
            <Form.Label>New Password</Form.Label>
            <Form.Control
              type="password"
              value={resetPassword.password}
              onChange={(e) => setResetPassword({ ...resetPassword, password: e.target.value })}
              placeholder="Enter new password"
              required
              minLength={8}
            />
            <Form.Text>
              Password must be at least 8 characters long
            </Form.Text>
          </FormGroup>
          <FormGroup>
            <Form.Label>Confirm New Password</Form.Label>
            <Form.Control
              type="password"
              value={resetPassword.confirmPassword}
              onChange={(e) => setResetPassword({ ...resetPassword, confirmPassword: e.target.value })}
              placeholder="Confirm new password"
              required
              minLength={8}
            />
          </FormGroup>
          <ButtonGroup>
            <CustomButton 
              variant="secondary" 
              onClick={() => {
                setShowResetModal(false);
                setResetPassword({
                  password: '',
                  confirmPassword: ''
                });
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton 
              variant="primary" 
              type="submit" 
              disabled={loading || !resetPassword.password || !resetPassword.confirmPassword}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </CustomButton>
          </ButtonGroup>
        </StyledForm>
      </Modal>

      {/* Delete User Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUser(null);
        }}
        title={`Delete User: ${selectedUser?.name}`}
      >
        <div>
          <p>Are you sure you want to delete this user? This action cannot be undone.</p>
          <ButtonGroup>
            <CustomButton 
              variant="secondary" 
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedUser(null);
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton 
              variant="danger" 
              onClick={handleDeleteUser}
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete User'}
            </CustomButton>
          </ButtonGroup>
        </div>
      </Modal>
    </UserManagementContainer>
  );
};

export default UserManagement; 